import moment from "moment";
import _ from "lodash";

import {
  ASSET_MODAL_TYPE_NEW, ASSETS_LIABILITIES_TYPES,
  BUILDING_SAVING_ID,
  CALL_MONEY_CATEGORY,
  CONSTRUCTION_LOAN_CONTRACT_ID,
  LOAN_CATEGORY
} from "../constants";
import {
  DATE_MUST_BE_IN_PAST,
  FIELD_REQUIRED_MSG,
  WRONG_DATE_FORMAT_MSG
} from "../../RiskProfiling/constants";
import {isValidValue} from "../../RiskProfiling/utils";
import {dateToMoment, momentToDate} from "./utils";
import {getExternalContractsSetting} from "../../../utils/utils";
import {CATEGORY_DISABLED_INFO} from "./constants";

const BUILDING_SAVING_PHASE = {
  ASSETS: {
    id: 2,
    value: "2",
    label: 'Guthaben'
  },
  LIABILITY: {
    id: 1,
    value: "1",
    label: 'Darlehen'
  }
};

const PHASE_OPTIONS = Object.values(BUILDING_SAVING_PHASE);
const BUILDING_SAVING_PHASE_LABEL = 'Bausparart';
const BUILDING_SAVING_PHASE_MODEL_FIELD = 'contractItem.savingsPlanKindId';

export const FIELD_TYPES = {
  select: 'select',
  amount: 'amount',
  date: 'date',
  percentage: 'percentage',
  number: 'number',
  toggle: 'toggle',
  group: 'group'
};

export function getSchemeFields(scheme, formData, entity){
  return !scheme.dynamic ? scheme.fields : scheme.dynamic.build(scheme.fields, formData, entity)
}

function* flattenFields(fields){
  for (const f of fields){
    if (f.type === FIELD_TYPES.group) {
      yield* flattenFields(f.fields)
    } else {
      yield f
    }
  }
}

export function schemeFlattenFields(scheme){
  return flattenFields(scheme.fields)
}

export function validateScheme(scheme, formData) {
  const errors = {};

  const fields = flattenFields(getSchemeFields(scheme, formData, {}))

  fields.forEach(f => {
    const err = validateField(f, formData);
    if(err){
      _.set(errors, f.modelField, err);
    }
  });

  return errors;
}

function validateField(field, formData) {
  const value = _.get(formData, field.modelField);

  if (field.required && !isValidValue(value)) {
    return FIELD_REQUIRED_MSG;
  } else if(field.type === FIELD_TYPES.date && !!value) {
    const date = moment(value);
    let error;
    if (!date.isValid()) {
      error = WRONG_DATE_FORMAT_MSG;
    } else if (field.disableFuture && date.isAfter(moment(), 'day')) {
      error = DATE_MUST_BE_IN_PAST;
    } else if(field.minDateField){
      const minDate = dateToMoment(formData[field.minDateField]);
      if(moment.isMoment(minDate) && minDate.isValid() && minDate.diff(date, 'days') > 0){
        error = field.minError;
      }
    } else if(field.maxDateField){
      const maxDate = dateToMoment(formData[field.maxDateField]);
      if(moment.isMoment(maxDate) && maxDate.isValid() && date.diff(maxDate, 'days') > 0){
        error = field.maxError;
      }
    }

    return error
  }
}

export function initFormDataForContractWithDefaults(dynamicFormScheme, newState) {
  schemeFlattenFields(dynamicFormScheme).forEach((f) => {
    if (f.value !== undefined) {
      _.set(newState, f.modelField, f.value)
    }
  });
  newState.updated = _.get(newState, UPDATED_FIELD.modelField)
}

const getMinDateErrorMsg = (startLabel) => `Das Datum muss nach dem "${startLabel}" liegen.`;
const getMaxDateErrorMsg = (endLabel) => `Das Datum muss vor dem "${endLabel}" liegen.`;

const convertToOptions = (obj) => {
  return Object.entries(obj).map(([key, value]) => ({
    id: key,
    value: key,
    label: value,
  }));
}


// "8": "monatlich", "4": "vierteljährlich", "2": "halbjährlich", "1": "jährlich", "6": "Einmalbeitrag"
const FREQ_OPTIONS_ORDER = ['8', '4', '2', '1', '6'];

const FREQ_FIELD = {
  label: "Zahlweise",
  modelField: 'contributionFrequency',
  type: FIELD_TYPES.select,
  options: [],
  optionsGetter: () => {
    const options = convertToOptions(getExternalContractsSetting('reference_data.ContributionFrequency'))

    return _.sortBy(options, (option) => {
      const index = FREQ_OPTIONS_ORDER.indexOf(option.value);
      return index === -1 ? Infinity : index;
    });
  },
  sizes: {xs:12, sm: 4},
}

const START_DATE_FIELD = {
  label: 'Beginn',
  modelField: 'contractBeginDate',
  type: FIELD_TYPES.date,
  disableFuture: true,
  sizes: {xs:12, sm: 4},
}

const END_DATE_FIELD = {
  label: 'Ende',
  modelField: 'contractEndDate',
  type: FIELD_TYPES.date,
  sizes: {xs:12, sm: 4},
  minDateField: START_DATE_FIELD.modelField,
  minError: getMinDateErrorMsg(START_DATE_FIELD.label),
}

const CONTRACT_TYPE_FIELD = {
  label: 'Vermittlungsart',
  modelField: 'contractType',
  type: FIELD_TYPES.select,
  options: [],
  optionsGetter: (viewOnly) => {
    const options = convertToOptions(getExternalContractsSetting('reference_data.InsuranceType'));

    return viewOnly ? options : options.filter(o => o.label !== "Sonstiges")
  },
  required: true,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_NUMBER_FIELD = {
  label: 'Vertrags-Nr.',
  modelField: 'contractNumber',
  dataId: 'contract_number-input',
  required: true,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_NAME_FIELD = {
  label: 'Name / Bezeichnung',
  modelField: 'contractItem.name',
  required: true,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_COMPANY_FIELD = {
  label: 'Gesellschaft',
  dataId: 'company_name-input',
  modelField: 'companyId',
  type: FIELD_TYPES.select,
  options: [],
  optionsGetter: () => getExternalContractsSetting('companies', []).map(o => ({
    id: o.id,
    value: String(o.id),
    label: o.name,
  })),
  required: true,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_INITIAL_LOAN_AMOUNT_FIELD = {
  label: "Ursprüngliche Darlehenssumme",
  modelField: "contractItem.originalLoanAmount",
  type: FIELD_TYPES.amount,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_LOAN_AMOUNT_FIELD = {
  label: "Aktueller Darlehensstand",
  modelField: "contractItem.currentLoanAmount",
  type: FIELD_TYPES.amount,
  required: true,
  sizes: {xs:12, sm: 4},
}

const CONTRACT_REPAYMENT_FIELD = {
  label: "Tilgungssatz p.a.",
  modelField: "contractItem.repaymentRatePercent",
  type: FIELD_TYPES.percentage,
  sizes: {xs:12, sm: 4},
}

const getBuildingSavingFields = (phase) => [
  CONTRACT_NAME_FIELD,
  CONTRACT_COMPANY_FIELD,
  {
    label: 'Zahlbeitrag',
    type: FIELD_TYPES.amount,
    modelField: 'contributionAmount',
    sizes: {xs:12, sm: 4},
  },
  FREQ_FIELD,
  START_DATE_FIELD,
  END_DATE_FIELD,
  {
    label: BUILDING_SAVING_PHASE_LABEL,
    modelField: BUILDING_SAVING_PHASE_MODEL_FIELD,
    type: FIELD_TYPES.select,
    options: PHASE_OPTIONS,
    required: true,
    disabled: phase === BUILDING_SAVING_PHASE.LIABILITY.value,
    sizes: {xs:12, sm: 4},
    value: phase,  // default value
  },
  CONTRACT_NUMBER_FIELD,
  CONTRACT_TYPE_FIELD,
  {
    label: 'Steuerlich relevanter Beginn',
    modelField: 'contractItem.taxRelevantStartDate',
    type: FIELD_TYPES.date,
    sizes: {xs:12, sm: 4},
    maxDateField: END_DATE_FIELD.modelField,
    maxError: getMaxDateErrorMsg(END_DATE_FIELD.label)
  },
  {
    label: 'Bausparsumme',
    modelField: 'contractItem.targetSavingsAmount',
    type: FIELD_TYPES.amount,
    sizes: {xs:12, sm: 4},
  },
];

const UPDATED_FIELD = {
  label: "Stand vom",
  type: FIELD_TYPES.date,
  modelField: 'contractItem.statementDate',
  required: true,
  value: momentToDate(moment()),
  disabled: true,
  sizes: {xs:12, sm: 4},
};

const BUILDING_SAVINGS_EXTRA_FIELDS = [
  {
    label: "Vorgemerkte WOP",
    type: FIELD_TYPES.amount,
    modelField: 'contractItem.reservedHousingBonus',
    sizes: {xs:12, sm: 4},
  },
  {
    sizes: {xs:12, sm: 8, md: 4},
    type: FIELD_TYPES.group,
    dataId: 'toggle-group',
    fields: [
      {
        label: "Riester Zulage",
        type: FIELD_TYPES.toggle,
        modelField: 'contractItem.hasRiesterAllowance',
        sizes: {xs: 'auto'},
        readOnlyInputSize: 7, // chars
      },
      {
        ...UPDATED_FIELD,
        sizes: {xs: true},
      }
    ]
  }
];

export const FORMS_SCHEMES = {
  [`${ASSET_MODAL_TYPE_NEW.CASH}_${BUILDING_SAVING_ID}`]: {
    fields: [
      {
        label:"Kategorie",
        dataId: 'category-input',
        modelField: 'sub_category',
        type: FIELD_TYPES.select,
        options: CALL_MONEY_CATEGORY.subCategories,
        disabled: true,
        disabledReasonForCreate: CATEGORY_DISABLED_INFO,
        required: true,
        hideOnViewOnly: true,
        sizes: {xs:12},
      },
      ...getBuildingSavingFields(BUILDING_SAVING_PHASE.ASSETS.value),
      {
        label: "Zinssatz Guthaben",
        modelField: "contractItem.creditInterestRate",
        type: FIELD_TYPES.percentage,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Aktueller Guthabenstand",
        modelField: "contractItem.currentSavingsAmount",
        type: FIELD_TYPES.amount,
        required: true,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Letzte Einzahlung",
        modelField: "contractItem.lastPaymentDate",
        type: FIELD_TYPES.date,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Umsätze",
        modelField: "contractItem.turnover",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "VL Umsätze im lfd. Jahr",
        modelField: "contractItem.vlTurnoverCurrentYear",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Zuteilung möglich bei Aufzahlung von",
        modelField: "contractItem.allocationPossibleWithTopUpFrom",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Erreichter Anspargrad",
        modelField: "contractItem.reachedSavingsRatePercent",
        type: FIELD_TYPES.percentage,
        sizes: {xs:12, sm: 4},
      },
      ...BUILDING_SAVINGS_EXTRA_FIELDS,
    ],
    dynamic: {
      deps: [BUILDING_SAVING_PHASE_MODEL_FIELD],
      build(fields, formData, entity) {
        let dynamicFields = fields;

        if (_.get(formData, BUILDING_SAVING_PHASE_MODEL_FIELD) === BUILDING_SAVING_PHASE.LIABILITY.value){
           dynamicFields = FORMS_SCHEMES[`${ASSET_MODAL_TYPE_NEW.LOAN}_${BUILDING_SAVING_ID}`].fields;
        }

        return dynamicFields.map(field =>
          field.modelField === BUILDING_SAVING_PHASE_MODEL_FIELD
            ? {...field, disabled: !entity.id}
            : field
        );
      }
    }
  },
  [`${ASSET_MODAL_TYPE_NEW.LOAN}_${BUILDING_SAVING_ID}`]: {
    fields: [
      {
        label:"Kategorie",
        dataId: 'category-input',
        modelField: 'sub_category',
        type: FIELD_TYPES.select,
        options: LOAN_CATEGORY.subCategories,
        disabled: true,
        disabledReasonForCreate: CATEGORY_DISABLED_INFO,
        required: true,
        hideOnViewOnly: true,
        sizes: {xs:12},
      },
      ...getBuildingSavingFields(BUILDING_SAVING_PHASE.LIABILITY.value),
      CONTRACT_INITIAL_LOAN_AMOUNT_FIELD,
      CONTRACT_LOAN_AMOUNT_FIELD,
      {
        label: "Zinssatz Darlehen",
        modelField: "contractItem.loanInterestRate",
        type: FIELD_TYPES.percentage,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Zinsbindungsdatum",
        modelField: "contractItem.loanInterestFixedDate",
        type: FIELD_TYPES.date,
        sizes: {xs:12, sm: 4},
        minDateField: START_DATE_FIELD.modelField,
        minError: getMinDateErrorMsg(START_DATE_FIELD.label),
      },
      CONTRACT_REPAYMENT_FIELD,
      {
        label: "Monatliche Tilgungssumme",
        modelField: "contractItem.monthlyRepaymentRate",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      ...BUILDING_SAVINGS_EXTRA_FIELDS
    ]
  },
  [`${ASSET_MODAL_TYPE_NEW.LOAN}_${CONSTRUCTION_LOAN_CONTRACT_ID}`]: {
    fields: [
      {
        label:"Kategorie",
        dataId: 'category-input',
        modelField: 'sub_category',
        type: FIELD_TYPES.select,
        options: LOAN_CATEGORY.subCategories,
        disabled: true,
        disabledReasonForCreate: CATEGORY_DISABLED_INFO,
        required: true,
        hideOnViewOnly: true,
        sizes: {xs:12},
      },
      CONTRACT_NAME_FIELD,
      CONTRACT_COMPANY_FIELD,
      {
        label: "Zahlbeitrag / Monatliche Annuität",
        modelField: "contributionAmount",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      FREQ_FIELD,
      START_DATE_FIELD,
      END_DATE_FIELD,
      {
        label: "Darlehensform",
        modelField: 'contractItem.loanTypeId',
        type: FIELD_TYPES.select,
        options: [],
        optionsGetter: () => convertToOptions(getExternalContractsSetting('reference_data.LoanType')),
        sizes: {xs:12, sm: 4},
      },
      CONTRACT_NUMBER_FIELD,
      CONTRACT_TYPE_FIELD,
      CONTRACT_INITIAL_LOAN_AMOUNT_FIELD,
      CONTRACT_LOAN_AMOUNT_FIELD,
      {
        label: "Zinssatz p.a.",
        modelField: "contractItem.interestRatePercent",
        type: FIELD_TYPES.percentage,
        sizes: {xs:12, sm: 4},
      },
      {
        // expiry logic
        label: "Zinsbindungsdatum",
        modelField: "contractItem.fixedInterestPeriodEndDate",
        type: FIELD_TYPES.date,
        sizes: {xs:12, sm: 4},
        minDateField: START_DATE_FIELD.modelField,
        minError: getMinDateErrorMsg(START_DATE_FIELD.label),
      },
      {
        label: "Zinsbindung in Jahren",
        modelField: "contractItem.fixedInterestPeriodYears",
        type: FIELD_TYPES.number,
        sizes: {xs:12, sm: 4},
      },
      CONTRACT_REPAYMENT_FIELD,
      {
        label: "Monatliche Tilgungssumme",
        modelField: "contractItem.monthlyRepaymentAmount",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Kündigungsrecht §489 BGB zum",
        modelField: "contractItem.cancellationRightUntil",
        type: FIELD_TYPES.date,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Sondertilgungsmöglichkeiten",
        modelField: "contractItem.specialRepaymentPercent",
        type: FIELD_TYPES.percentage,
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Vorrauss. Restdarlehen nach ZB",
        modelField: "contractItem.remainingLoanAfterFixedInterest",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: 'Mittelverwendung',
        modelField: 'contractItem.loanUsageId',
        type: FIELD_TYPES.select,
        options: [],
        optionsGetter: () => convertToOptions(getExternalContractsSetting('reference_data.LoanUsageType')),
        sizes: {xs:12, sm: 4},
      },
      {
        label: 'Art der Immobilie',
        modelField: 'contractItem.propertyTypeId',
        type: FIELD_TYPES.select,
        options: [],
        optionsGetter: () => convertToOptions(getExternalContractsSetting('reference_data.PropertyType')),
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Ermittelter Objektwert / Kaufpreis",
        modelField: "contractItem.propertyValueOrPurchasePrice",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      {
        label: 'Art der Nutzung',
        modelField: 'contractItem.propertyUsageId',
        type: FIELD_TYPES.select,
        options: [],
        optionsGetter: () => convertToOptions(getExternalContractsSetting('reference_data.PropertyUsageType')),
        sizes: {xs:12, sm: 4},
      },
      {
        label: "Beleihungsauslauf",
        modelField: "contractItem.loanToValueRatio",
        type: FIELD_TYPES.amount,
        sizes: {xs:12, sm: 4},
      },
      UPDATED_FIELD
    ]
  }
};

export const getFormScheme = (categoryId, subCategory, objTypeId) => FORMS_SCHEMES[`${categoryId}_${subCategory}`] || FORMS_SCHEMES[objTypeId]

export const getNewCategoryToMove = (item, currentCategory) => {
  if(item.type_id === BUILDING_SAVING_ID && currentCategory === ASSET_MODAL_TYPE_NEW.CASH && _.get(item, BUILDING_SAVING_PHASE_MODEL_FIELD) === BUILDING_SAVING_PHASE.LIABILITY.value){
    return {categoryId: ASSET_MODAL_TYPE_NEW.LOAN, itemType: ASSETS_LIABILITIES_TYPES.LIABILITIES, fieldChanged: BUILDING_SAVING_PHASE_LABEL};
  }
}